/**
 * The most attack units one cast can release through stage events.
 *
 * A stage released by an event is released as often as the event happens, which
 * the plan alone cannot say (a zone pulses, a bolt may pierce, a swing may hit
 * ten targets). The cast's attack units are therefore limited as a whole: the
 * plain stages' units plus every event-released unit never exceed `cap`, and
 * `executeCompositeCast` stops releasing event stages at what is left. The
 * static gates read the same bound here, so a plan is judged for the workload
 * it can actually produce.
 */
import { resolveLegacyDeliveryForApplication } from './application.js';
import { expandSpatialPattern } from './spatial-pattern.js';
import type { AttackStageV1, CanonicalSkillV3Input, StageEventPerV1, StageEventV1 } from './composite-types.js';
import type { SkillApplicationV2 } from './types.js';

export interface EventUnitBound {
  /** Attack units this stage releases per cast, worst case, for a stage released by an event. */
  readonly eventUnitsByStageId: ReadonlyMap<string, number>;
  /** Attack units of every stage released by a plain timing. */
  readonly staticUnits: number;
  /** What is left of the cast's limit for event-released units. */
  readonly eventUnitLimit: number;
}

/** The code for a stage event no price model counts. */
export const STAGE_EVENT_COUNT_MODEL_UNAVAILABLE = 'COMPOSITE_STAGE_EVENT_COUNT_MODEL_UNAVAILABLE';

/**
 * Why how often a stage event happens cannot be priced from its timing alone, or
 * `undefined` when it can. An every-n-th counter per unit or per enemy fires only
 * where that one unit or enemy reaches n hits, which needs the distribution of
 * each one's hits, not their expectation: three one-hit bolts with an every-3rd
 * counter per unit never fire. No such distribution is modelled, so a plan that
 * needs it requires the closed capability `STAGE_EVENT_SCOPED_EVERY_N`, which
 * every path to a castable skill checks (`assessCompositeCapability`), and the
 * count model (`eval/stage-event-hits.ts`) refuses it the same way.
 */
export function uncountableStageEvent(timing: { readonly event: StageEventV1; readonly per: StageEventPerV1; readonly n?: number }): string | undefined {
  if (timing.event !== 'EVERY_N' || timing.per === 'CAST') return undefined;
  return `every ${timing.n}-th hit is counted per ${timing.per.toLowerCase()}, and no model gives how often one ${timing.per === 'UNIT' ? 'unit' : 'enemy'} alone reaches ${timing.n} hits`;
}

/** What the cast's limit leaves for event-released units once its plain stages are counted. */
export function eventUnitLimit(staticUnits: number, cap: number): number {
  return Math.max(0, cap - staticUnits);
}

/**
 * The most a per-unit quantity (units, footprints, emissions) summed over every
 * event stage can reach, when all of them draw on the one budget `limit` the
 * runtime gives a cast's event releases (`executeCompositeCast`'s
 * `identity.budget`): the budget goes to the heaviest units first, each stage up
 * to its own most. Filling fractionally makes this an upper bound.
 */
export function sharedEventBudgetWorstCase(
  stages: readonly { readonly maxUnits: number; readonly perUnit: number }[],
  limit: number,
): number {
  let left = limit;
  let total = 0;
  for (const stage of [...stages].sort((a, b) => b.perUnit - a.perUnit)) {
    const taken = Math.min(left, stage.maxUnits);
    total += taken * stage.perUnit;
    left -= taken;
    if (left <= 0) break;
  }
  return total;
}

/**
 * The most enemies one unit of an attack strikes in one application, where the runtime itself bounds it, else infinity:
 * a projectile's `maxPierce` (`?? 1`, the bodies it passes through in all: `AttackInstancePool.spawn` starts
 * `remainingPierce` there and destroys the projectile at 0; a falloff pierce has no count), a weapon contact's
 * `contactPolicy.maxPierce` (`weapon-contact.ts`, across all frames of a sweep: `weapon-sweep.ts`). An area has no
 * per-unit cap. The one rule every static reader of a stage's contacts shares.
 */
export function maxContactsPerUnit(application: SkillApplicationV2): number {
  if (application.geometry.owner === 'WEAPON_CONTACT') return application.contactPolicy.maxPierce ?? Number.POSITIVE_INFINITY;
  const lowered = resolveLegacyDeliveryForApplication(application);
  if (!lowered.ok || lowered.delivery.type !== 'projectile' || lowered.delivery.pierceFalloff !== undefined) return Number.POSITIVE_INFINITY;
  return lowered.delivery.maxPierce ?? 1;
}

export function eventUnitBound(skill: CanonicalSkillV3Input, castSeed: number, cap: number): EventUnitBound {
  const unitsOf = new Map<string, number>();
  let staticUnits = 0;
  for (const stage of skill.stages) {
    const expanded = expandSpatialPattern(stage.pattern, castSeed);
    const units = expanded.ok ? expanded.units.length : 0;
    unitsOf.set(stage.stageId, units);
    if (stage.timing.kind !== 'ON_STAGE_EVENT' && stage.timing.kind !== 'ON_STATUS') staticUnits += units;
  }
  const limit = eventUnitLimit(staticUnits, cap);

  const firingsOf = new Map<string, number>();
  const eventUnitsByStageId = new Map<string, number>();
  const byId = new Map<string, AttackStageV1>(skill.stages.map((stage) => [stage.stageId, stage] as const));
  for (const stage of skill.stages) {
    const timing = stage.timing;
    if (timing.kind === 'ON_STATUS') {
      // Released as often as the status is applied, ticks or ends at an enemy, whoever stands there.
      firingsOf.set(stage.stageId, limit);
      eventUnitsByStageId.set(stage.stageId, Math.min(limit, limit * unitsOf.get(stage.stageId)!));
      continue;
    }
    if (timing.kind !== 'ON_STAGE_EVENT') {
      firingsOf.set(stage.stageId, 1);
      continue;
    }
    const reference = byId.get(timing.referenceStageId)!;
    const referenceOnce = unitsOf.get(reference.stageId)! * firingsOf.get(reference.stageId)!;
    // Countable from the plan: once per cast, and once per unit (its end, or its first contact).
    // Every contact and every enemy depend on who is standing there: on the cast limit, or on the most contacts the
    // reference's units can make where the runtime bounds that (`maxContactsPerUnit`, a bolt's pierce).
    // A sweep (a detonation of marks, or of a skill status's carriers) is released once per carrier, whoever stands there.
    const firings = timing.anchor === 'MARKED' || timing.anchor === 'CARRIERS'
      ? limit
      : timing.per === 'CAST'
      ? 1
      : timing.per === 'UNIT' || (timing.event === 'END')
        ? Math.min(limit, referenceOnce)
        : contactFirings(reference, referenceOnce, limit);
    firingsOf.set(stage.stageId, firings);
    eventUnitsByStageId.set(stage.stageId, Math.min(limit, firings * unitsOf.get(stage.stageId)!));
  }
  return { eventUnitsByStageId, staticUnits, eventUnitLimit: limit };
}

/** Firings of a contact- or enemy-scoped event: what the reference's units can make, never past the cast limit; an orbit or an area makes as many as the limit. */
function contactFirings(reference: AttackStageV1, referenceOnce: number, limit: number): number {
  const perUnit = reference.motion !== undefined ? Number.POSITIVE_INFINITY : maxContactsPerUnit(reference.application);
  return Number.isFinite(perUnit) ? Math.min(limit, referenceOnce * perUnit) : limit;
}
